<template>
  <b-overlay
      :show="isLoading"
      rounded="sm"
      opacity="0.65"
  >
    <div class="settings-page">
      <b-card class="settings-header mb-2">
        <h1 class="settings-title">
          Profit Distribution Settings
        </h1>
      </b-card>

      <div class="section-switcher mb-2">
        <b-button
            :variant="activeSection === 'middleMan' ? 'primary' : 'outline-primary'"
            class="section-button"
            @click="activeSection = 'middleMan'"
        >
          Middle Man Share
        </b-button>

        <b-button
            :variant="activeSection === 'system' ? 'primary' : 'outline-primary'"
            class="section-button"
            @click="activeSection = 'system'"
        >
          System Distribution
        </b-button>
      </div>

      <b-alert
          v-if="loadError"
          show
          variant="danger"
          class="mb-2"
      >
        {{ loadError }}
      </b-alert>

      <template v-else>
        <b-card
            v-if="activeSection === 'middleMan' && middleManSetting"
            class="setting-card"
        >
          <div class="section-header">
            <div>
              <h3 class="section-title">
                Middle Man Share
              </h3>
            </div>
          </div>

          <div class="setting-box">
            <div class="setting-top">
              <span class="setting-label">
                Middle Man Percentage
              </span>

              <div class="percentage-input-wrapper">
                <b-form-input
                    v-model.number="middleManSetting.value"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    class="percentage-input"
                    :disabled="isMiddleManSaving"
                    @blur="clampValue(middleManSetting)"
                />

                <span class="percentage-symbol">
                  %
                </span>
              </div>
            </div>

            <b-form-input
                v-model.number="middleManSetting.value"
                type="range"
                min="0"
                max="100"
                step="1"
                class="setting-range"
                :disabled="isMiddleManSaving"
                :style="getRangeStyle(middleManSetting.value)"
            />

            <div class="range-labels">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>

          <b-button
              variant="primary"
              block
              class="save-button"
              :disabled="!middleManChanged || isMiddleManSaving"
              @click="updateMiddleMan"
          >
            <b-spinner
                v-if="isMiddleManSaving"
                small
                class="mr-50"
            />

            {{ isMiddleManSaving ? 'Saving...' : 'Save Middle Man Share' }}
          </b-button>
        </b-card>

        <b-card
            v-if="activeSection === 'system' && systemSettings.length"
            class="setting-card"
        >
          <div class="section-header">
            <div>
              <h3 class="section-title">
                System Distribution
              </h3>
            </div>

            <div
                class="total-badge"
                :class="isSystemValid ? 'total-badge-valid' : 'total-badge-invalid'"
            >
              Total: {{ systemTotal }}%
            </div>
          </div>

          <b-alert
              show
              :variant="isSystemValid ? 'success' : 'warning'"
              class="policy-alert"
          >
            <div class="policy-content">
              <feather-icon
                  :icon="isSystemValid ? 'CheckCircleIcon' : 'AlertTriangleIcon'"
                  size="20"
                  class="policy-icon"
              />

              <div>
                <strong>
                  {{ isSystemValid ? 'Distribution is valid' : 'Distribution policy' }}
                </strong>

                <div class="mt-25">
                  Platform Fee Percentage, Booster Percentage Normal Order and
                  Admin Percentage Normal Order must total exactly
                  <strong>100%</strong>.
                </div>

                <div
                    v-if="!isSystemValid"
                    class="mt-50 font-weight-bold"
                >
                  {{ systemDifferenceText }}
                </div>
              </div>
            </div>
          </b-alert>

          <div
              v-if="!isSystemValid"
              class="auto-balance-box mb-2"
          >
            <div class="auto-balance-content">
              <div>
                <strong class="auto-balance-title">
                  Total must be 100%
                </strong>

                <p class="auto-balance-description">
                  Automatically adjust the current percentages while keeping their distribution as close as possible.
                </p>
              </div>

              <b-button
                  variant="outline-primary"
                  class="auto-balance-button"
                  :disabled="isSystemSaving"
                  @click="autoBalanceSystem"
              >
                <feather-icon
                    icon="RefreshCwIcon"
                    size="16"
                    class="mr-50"
                />

                Auto Balance to 100%
              </b-button>
            </div>
          </div>

          <div class="system-settings-grid">
            <div
                v-for="setting in systemSettings"
                :key="setting.option"
                class="setting-box"
            >
              <div class="setting-top">
                <span class="setting-label">
                  {{ getOptionLabel(setting.option) }}
                </span>

                <div class="percentage-input-wrapper">
                  <b-form-input
                      v-model.number="setting.value"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      class="percentage-input"
                      :disabled="isSystemSaving"
                      @blur="clampValue(setting)"
                  />

                  <span class="percentage-symbol">
                    %
                  </span>
                </div>
              </div>

              <b-form-input
                  v-model.number="setting.value"
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  class="setting-range"
                  :disabled="isSystemSaving"
                  :style="getRangeStyle(setting.value)"
              />

              <div class="range-labels">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          <div class="system-summary">
            <div
                v-for="setting in systemSettings"
                :key="`summary-${setting.option}`"
                class="summary-item"
            >
              <span>
                {{ getOptionLabel(setting.option) }}
              </span>

              <strong>
                {{ setting.value }}%
              </strong>
            </div>

            <div
                class="summary-item summary-total"
                :class="{ 'summary-total-invalid': !isSystemValid }"
            >
              <span>
                Total
              </span>

              <strong>
                {{ systemTotal }}%
              </strong>
            </div>
          </div>

          <div
              v-if="!isSystemValid"
              class="invalid-total-message"
          >
            <feather-icon
                icon="AlertCircleIcon"
                size="17"
            />

            <span>
              Save is disabled until the total equals exactly 100%.
            </span>
          </div>

          <b-button
              variant="primary"
              block
              class="save-button"
              :disabled="!canSaveSystem"
              @click="updateSystem"
          >
            <b-spinner
                v-if="isSystemSaving"
                small
                class="mr-50"
            />

            {{ isSystemSaving ? 'Saving...' : 'Save System Distribution' }}
          </b-button>
        </b-card>
      </template>
    </div>
  </b-overlay>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BFormInput,
  BOverlay,
  BSpinner,
} from 'bootstrap-vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import {
  GetGenericSettings,
  UpdateGenericSetting,
} from '@/libs/Api/SiteSettings'

const MIDDLE_MAN_OPTION = 'MiddleManPercentage'

const SYSTEM_OPTIONS = [
  'PlatformFeePercentage',
  'BoosterPercentageNormalOrder',
  'AdminPercentageNormalOrder',
]

const ALLOWED_OPTIONS = [
  MIDDLE_MAN_OPTION,
  ...SYSTEM_OPTIONS,
]

const OPTION_LABELS = {
  MiddleManPercentage: 'Middle Man Percentage',
  PlatformFeePercentage: 'Platform Fee Percentage',
  BoosterPercentageNormalOrder: 'Booster Percentage Normal Order',
  AdminPercentageNormalOrder: 'Admin Percentage Normal Order',
}

export default {
  name: 'GeneralSetting',

  components: {
    BAlert,
    BButton,
    BCard,
    BFormInput,
    BOverlay,
    BSpinner,
  },

  data() {
    return {
      activeSection: 'middleMan',
      isLoading: false,
      isMiddleManSaving: false,
      isSystemSaving: false,
      loadError: '',
      settings: [],
    }
  },

  computed: {
    middleManSetting() {
      return this.settings.find(
          setting => setting.option === MIDDLE_MAN_OPTION,
      ) || null
    },

    systemSettings() {
      return SYSTEM_OPTIONS
          .map(option => this.settings.find(
              setting => setting.option === option,
          ))
          .filter(Boolean)
    },

    systemTotal() {
      return this.systemSettings.reduce(
          (total, setting) => total + this.toPercentage(setting.value),
          0,
      )
    },

    isSystemValid() {
      return (
          this.systemSettings.length === SYSTEM_OPTIONS.length
          && this.systemTotal === 100
      )
    },

    systemDifferenceText() {
      if (this.systemTotal > 100) {
        return `The total is ${this.systemTotal - 100}% over the allowed value.`
      }

      return `${100 - this.systemTotal}% is still unallocated.`
    },

    middleManChanged() {
      if (!this.middleManSetting) {
        return false
      }

      return (
          this.toPercentage(this.middleManSetting.value)
          !== this.toPercentage(this.middleManSetting.originalValue)
      )
    },

    systemChanged() {
      return this.systemSettings.some(
          setting => (
              this.toPercentage(setting.value)
              !== this.toPercentage(setting.originalValue)
          ),
      )
    },

    canSaveSystem() {
      return (
          this.isSystemValid
          && this.systemChanged
          && !this.isSystemSaving
      )
    },
  },

  async created() {
    await this.getSettings()
  },

  methods: {
    executeRequest(request) {
      return new Promise((resolve, reject) => {
        request.fetch(resolve, reject)
      })
    },

    async getSettings() {
      this.isLoading = true
      this.loadError = ''

      try {
        const request = new GetGenericSettings(this)

        request.setParams({
          pageNumber: 1,
          count: 100,
        })

        const content = await this.executeRequest(request)

        if (
            !content?.isSuccess
            || !Array.isArray(content?.data?.data)
        ) {
          throw new Error(
              content?.message || 'Settings could not be loaded.',
          )
        }

        this.settings = content.data.data
            .filter(setting => ALLOWED_OPTIONS.includes(setting.option))
            .map(setting => {
              const value = this.toPercentage(setting.value)

              return {
                id: setting.id,
                option: setting.option,
                value,
                originalValue: value,
              }
            })
      } catch (error) {
        this.loadError = this.getErrorMessage(
            error,
            'Settings could not be loaded.',
        )

        this.showToast(
            'error',
            this.loadError,
        )
      } finally {
        this.isLoading = false
      }
    },

    async updateMiddleMan() {
      if (
          !this.middleManSetting
          || !this.middleManChanged
          || this.isMiddleManSaving
      ) {
        return
      }

      this.clampValue(this.middleManSetting)
      this.isMiddleManSaving = true

      try {
        await this.updateSetting(this.middleManSetting)

        this.middleManSetting.originalValue =
            this.toPercentage(this.middleManSetting.value)

        this.showToast(
            'success',
            'Middle man share updated successfully.',
        )
      } catch (error) {
        this.showToast(
            'error',
            this.getErrorMessage(error),
        )
      } finally {
        this.isMiddleManSaving = false
      }
    },

    async updateSystem() {
      if (!this.canSaveSystem) {
        return
      }

      this.systemSettings.forEach(setting => {
        this.clampValue(setting)
      })

      if (!this.isSystemValid) {
        return
      }

      this.isSystemSaving = true

      try {
        await Promise.all(
            this.systemSettings.map(
                setting => this.updateSetting(setting),
            ),
        )

        this.systemSettings.forEach(setting => {
          setting.originalValue = this.toPercentage(setting.value)
        })

        this.showToast(
            'success',
            'System distribution updated successfully.',
        )
      } catch (error) {
        this.showToast(
            'error',
            this.getErrorMessage(error),
        )
      } finally {
        this.isSystemSaving = false
      }
    },

    async updateSetting(setting) {
      const request = new UpdateGenericSetting(this)

      request.setParams({
        option: setting.option,
        value: String(this.toPercentage(setting.value)),
      })

      const content = await this.executeRequest(request)

      if (!content?.isSuccess) {
        throw new Error(
            content?.message || 'Could not update setting.',
        )
      }

      return content
    },

    autoBalanceSystem() {
      if (!this.systemSettings.length || this.isSystemSaving) {
        return
      }

      const values = this.systemSettings.map(
          setting => this.toPercentage(setting.value),
      )

      const total = values.reduce(
          (sum, value) => sum + value,
          0,
      )

      if (total === 100) {
        return
      }

      if (total === 0) {
        const balancedValues = [34, 33, 33]

        this.systemSettings.forEach((setting, index) => {
          setting.value = balancedValues[index]
        })

        return
      }

      const scaledValues = values.map(
          value => (value / total) * 100,
      )

      const roundedDown = scaledValues.map(
          value => Math.floor(value),
      )

      let remainder = 100 - roundedDown.reduce(
          (sum, value) => sum + value,
          0,
      )

      const fractions = scaledValues
          .map((value, index) => ({
            index,
            fraction: value - Math.floor(value),
          }))
          .sort((a, b) => b.fraction - a.fraction)

      const balancedValues = [...roundedDown]

      let fractionIndex = 0

      while (remainder > 0) {
        const targetIndex =
            fractions[fractionIndex % fractions.length].index

        balancedValues[targetIndex] += 1
        remainder -= 1
        fractionIndex += 1
      }

      this.systemSettings.forEach((setting, index) => {
        setting.value = balancedValues[index]
      })
    },

    clampValue(setting) {
      setting.value = this.toPercentage(setting.value)
    },

    toPercentage(value) {
      const number = Number(value)

      if (!Number.isFinite(number)) {
        return 0
      }

      return Math.min(
          100,
          Math.max(
              0,
              Math.round(number),
          ),
      )
    },

    getOptionLabel(option) {
      return OPTION_LABELS[option] || option
    },

    getRangeStyle(value) {
      return {
        '--range-progress': `${this.toPercentage(value)}%`,
      }
    },

    getErrorMessage(error, fallback = 'An unexpected error occurred.') {
      return (
          error?.response?.data?.message
          || error?.message
          || fallback
      )
    },

    showToast(type, text) {
      const isSuccess = type === 'success'

      this.$toast({
        component: ToastificationContent,
        position: 'bottom-center',
        props: {
          title: isSuccess ? 'Success' : 'Error',
          icon: isSuccess ? 'CheckIcon' : 'XIcon',
          variant: isSuccess ? 'success' : 'danger',
          text,
        },
      })
    },
  },
}
</script>

<style scoped>
.settings-page {
  width: 100%;
  margin-top: 30px;
}

.settings-header {
  border: 1px solid #ebe9f1;
  border-radius: 16px;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.06);
}

.settings-title {
  margin: 0;
  color: #2c2c2c;
  font-size: 1.55rem;
  font-weight: 700;
}

.settings-description {
  margin: 0.4rem 0 0;
  color: #6e6b7b;
  font-size: 0.9rem;
}

.section-switcher {
  display: flex;
  width: fit-content;
  padding: 0.35rem;
  gap: 0.35rem;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  background: #fff;
}

.section-button {
  min-width: 175px;
  border-radius: 10px;
  font-weight: 600;
}

.setting-card {
  border: 1px solid #ebe9f1;
  border-radius: 16px;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.06);
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.section-title {
  margin: 0;
  color: #2c2c2c;
  font-size: 1.2rem;
  font-weight: 700;
}

.section-description {
  max-width: 650px;
  margin: 0.4rem 0 0;
  color: #6e6b7b;
  font-size: 0.88rem;
  line-height: 1.6;
}

.percentage-badge,
.total-badge {
  display: flex;
  min-width: 100px;
  min-height: 46px;
  padding: 0.6rem 0.9rem;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
}

.percentage-badge {
  color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
}

.total-badge-valid {
  color: #28c76f;
  background: rgba(40, 199, 111, 0.1);
}

.total-badge-invalid {
  color: #ff9f43;
  background: rgba(255, 159, 67, 0.12);
}

.policy-alert {
  border-radius: 12px;
}

.policy-content {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
}

.policy-icon {
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.auto-balance-box {
  padding: 1rem;
  border: 1px dashed rgba(115, 103, 240, 0.35);
  border-radius: 14px;
  background: rgba(115, 103, 240, 0.04);
}

.auto-balance-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.auto-balance-title {
  display: block;
  margin-bottom: 0.25rem;
  color: #5e5873;
  font-size: 0.9rem;
}

.auto-balance-description {
  margin: 0;
  color: #82808b;
  font-size: 0.8rem;
  line-height: 1.5;
}

.auto-balance-button {
  flex-shrink: 0;
  border-radius: 10px;
  font-weight: 600;
}

.system-settings-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.setting-box {
  padding: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  background: #fafafd;
}

.setting-top {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.35rem;
}

.setting-label {
  color: #5e5873;
  font-size: 0.87rem;
  font-weight: 600;
  line-height: 1.4;
}

.percentage-input-wrapper {
  position: relative;
  width: 88px;
  flex: 0 0 88px;
}

.percentage-input {
  height: 38px;
  padding-right: 27px;
  border-color: #d8d6de;
  border-radius: 9px;
  color: #7367f0;
  background: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  text-align: center;
}

.percentage-input:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.12);
}

.percentage-symbol {
  position: absolute;
  top: 50%;
  right: 10px;
  color: #9a97a8;
  font-size: 0.8rem;
  font-weight: 600;
  pointer-events: none;
  transform: translateY(-50%);
}

.percentage-input::-webkit-inner-spin-button,
.percentage-input::-webkit-outer-spin-button {
  margin: 0;
}

.setting-range {
  width: 100%;
  height: 20px;
  margin: 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
}

.setting-range:focus {
  outline: none;
}

.setting-range:disabled {
  cursor: not-allowed;
}

.setting-range::-webkit-slider-runnable-track {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(
      to right,
      #7367f0 0%,
      #7367f0 var(--range-progress),
      #dedbe9 var(--range-progress),
      #dedbe9 100%
  );
}

.setting-range::-webkit-slider-thumb {
  width: 21px;
  height: 21px;
  margin-top: -6.5px;
  border: 3px solid #fff;
  border-radius: 50%;
  appearance: none;
  -webkit-appearance: none;
  background: #7367f0;
  cursor: grab;
  box-shadow:
      0 2px 7px rgba(115, 103, 240, 0.4),
      0 0 0 2px rgba(115, 103, 240, 0.08);
}

.setting-range::-webkit-slider-thumb:active {
  cursor: grabbing;
}

.setting-range::-moz-range-track {
  height: 8px;
  border-radius: 999px;
  background: #dedbe9;
}

.setting-range::-moz-range-progress {
  height: 8px;
  border-radius: 999px;
  background: #7367f0;
}

.setting-range::-moz-range-thumb {
  width: 15px;
  height: 15px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #7367f0;
  cursor: grab;
  box-shadow: 0 2px 7px rgba(115, 103, 240, 0.4);
}

.range-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 0.55rem;
  color: #9a97a8;
  font-size: 0.68rem;
}

.system-summary {
  overflow: hidden;
  margin-bottom: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 12px;
}

.summary-item {
  display: flex;
  min-height: 46px;
  padding: 0.75rem 1rem;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid #ebe9f1;
  color: #5e5873;
  font-size: 0.84rem;
}

.summary-item:last-child {
  border-bottom: 0;
}

.summary-item strong {
  color: #2c2c2c;
  font-size: 0.95rem;
}

.summary-total {
  background: rgba(40, 199, 111, 0.06);
  font-weight: 700;
}

.summary-total strong {
  color: #28c76f;
}

.summary-total-invalid {
  background: rgba(255, 159, 67, 0.08);
}

.summary-total-invalid strong {
  color: #ff9f43;
}

.invalid-total-message {
  display: flex;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  align-items: center;
  gap: 0.6rem;
  border-radius: 10px;
  color: #b86b19;
  background: rgba(255, 159, 67, 0.1);
  font-size: 0.82rem;
  font-weight: 600;
}

.save-button {
  min-height: 44px;
  border-radius: 10px;
  font-weight: 600;
}

@media (max-width: 991px) {
  .system-settings-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575px) {
  .settings-page {
    margin-top: 20px;
  }

  .settings-title {
    font-size: 1.3rem;
  }

  .section-switcher {
    width: 100%;
  }

  .section-button {
    flex: 1;
    min-width: 0;
    padding-right: 0.5rem;
    padding-left: 0.5rem;
    font-size: 0.8rem;
  }

  .section-header {
    flex-direction: column;
  }

  .percentage-badge,
  .total-badge {
    min-width: 0;
  }

  .auto-balance-content {
    align-items: stretch;
    flex-direction: column;
  }

  .auto-balance-button {
    width: 100%;
  }

  .setting-top {
    align-items: flex-start;
  }

  .summary-item {
    align-items: flex-start;
  }
}
</style>